"use strict";

import { CONSTANTS } from "~/helpers/constants";
import { cookie } from "~/services/cookies";

import type { CookiePurpose, Content, Rejected } from "~/types";

/**
 * Initialize the cookiedock cookie storage if it doesn't exist
 */
function initCookieStorage() {
  if (!window.cookiedock.__cookies) {
    window.cookiedock.__cookies = {
      rejected: [],
      content: null,
    };
  }
}

/**
 * Remove cookies that were rejected in the consent preferences
 */
export function removeRejectedCookies() {
  initCookieStorage();

  const rejectedCookieData = window.cookiedock.__cookies.rejected;
  if (!rejectedCookieData || rejectedCookieData.length === 0) return;

  const rejectedCookies = rejectedCookieData.flatMap((item) => item.cookies);

  if (rejectedCookies.length === 0) return;
  const rejectedCookieSet = new Set(rejectedCookies);

  document.cookie
    .split(";")
    .map((cookieStr) => cookieStr.trim())
    .filter((cookieStr) => cookieStr.length > 0)
    .forEach((cookieStr) => {
      const cookieName = cookieStr.split("=")[0]?.trim();
      if (!cookieName) return;
      if (rejectedCookieSet.has(cookieName)) {
        removeCookieWithAllPossibleOptions(cookieName);
      }
    });
}

/**
 * Remove a cookie with all possible path and domain combinations to ensure it's properly removed
 */
function removeCookieWithAllPossibleOptions(cookieName: string) {
  cookie.remove(cookieName);
  cookie.remove(cookieName, { path: "/" });

  const hostname = window.location.hostname;
  if (hostname.includes(".")) {
    const parts = hostname.split(".");
    if (parts.length >= 2) {
      const domain = parts.slice(-2).join(".");
      cookie.remove(cookieName, { domain, path: "/" });
    }
  }
}

/**
 * Check and remove all cookies on load without consent
 * @param cookiePurposes All cookie purposes from the backend
 */
export function removeAllCookiesWithoutConsent(cookiePurposes: CookiePurpose[]) {
  const cookieStatusMap = new Map<string, boolean>();

  cookiePurposes.forEach((purpose) => {
    const isMandatory = purpose.mandatory === true;
    purpose.detected_cookies.forEach((cookie) => {
      cookieStatusMap.set(cookie.cookie_name, isMandatory);
    });
  });

  document.cookie
    .split(";")
    .map((cookieStr) => cookieStr.trim())
    .filter((cookieStr) => cookieStr.length > 0)
    .forEach((cookieStr) => {
      const cookieName = cookieStr.split("=")[0]?.trim();
      if (!cookieName) return;
      if (cookieName !== CONSTANTS.DEFAULT.COOKIE_NAME && (!cookieStatusMap.has(cookieName) || !cookieStatusMap.get(cookieName))) {
        removeCookieWithAllPossibleOptions(cookieName);
      }
    });
}

/**
 * Store rejected cookies in the window.cookiedock.__cookies object
 */
export function storeRejectedCookies(rejectedCookies: Rejected[]) {
  initCookieStorage();
  window.cookiedock.__cookies.rejected = rejectedCookies;
  try {
    localStorage.setItem(CONSTANTS.REJECTED, JSON.stringify(rejectedCookies));
  } catch (e) {
    // ignore storage failures (private mode, quota, etc.)
  }
  removeRejectedCookies();
}

/**
 * Store content data in window.cookiedock.__cookies
 */
export function storeContentData(content: Content) {
  initCookieStorage();
  window.cookiedock.__cookies.content = content;
}

/**
 * Get content data from window.cookiedock.__cookies
 */
export function getContentData(): Content | null {
  initCookieStorage();
  return window.cookiedock.__cookies.content;
}

/**
 * Clear cookie storage data (used when reopening the consent banner)
 */
export function clearCookieStorage() {
  if (window.cookiedock.__cookies) {
    window.cookiedock.__cookies.rejected = [];
    window.cookiedock.__cookies.content = null;
  }

  try {
    localStorage.removeItem(CONSTANTS.REJECTED);
  } catch (e) {}

  try {
    localStorage.removeItem(CONSTANTS.DEFAULT.COOKIE_NAME);
  } catch (e) {}

  cookie.remove(CONSTANTS.DEFAULT.COOKIE_NAME, {
    path: "/",
    sameSite: "lax",
  });
}

/**
 * Save the user's consent choices to a persistent cookie
 * @param consentChoices Object containing user consent choices by category
 */
export function saveConsentState(consentChoices: Record<string, boolean>) {
  const consentData = {
    choices: consentChoices,
    timestamp: new Date().getTime(),
    version: window.cookiedock.__version || CONSTANTS.VERSION,
  };

  try {
    localStorage.setItem(CONSTANTS.DEFAULT.COOKIE_NAME, JSON.stringify(consentData));
  } catch (e) {
    // fallback to cookie if localStorage not available
    cookie.set(CONSTANTS.DEFAULT.COOKIE_NAME, consentData, {
      expires: 180,
      path: "/",
      sameSite: "lax",
    });
  }
}

/**
 * Load the user's consent choices from the persistent cookie
 * @returns The saved consent choices or null if not found
 */
export function loadConsentState(): { choices: Record<string, boolean>; timestamp: number; version: string } | null {
  try {
    const raw = localStorage.getItem(CONSTANTS.DEFAULT.COOKIE_NAME);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {}

  // migrate from legacy cookie storage if present
  const legacy = cookie.get<{ choices: Record<string, boolean>; timestamp: number; version: string }>(CONSTANTS.DEFAULT.COOKIE_NAME);
  if (legacy) {
    try {
      localStorage.setItem(CONSTANTS.DEFAULT.COOKIE_NAME, JSON.stringify(legacy));
      cookie.remove(CONSTANTS.DEFAULT.COOKIE_NAME, { path: "/" });
    } catch (e) {}
    return legacy;
  }

  return null;
}

/**
 * Apply the saved consent choices to restore scripts and event listeners
 * This should be called after the page has loaded and Cookiedock is initialized
 */
export function applyPersistedConsent() {
  const savedConsent = loadConsentState();
  if (!savedConsent) return false;

  const currentVersion = window.cookiedock.__version || CONSTANTS.VERSION;
  if (savedConsent.version !== currentVersion) {
    return false;
  }

  const categories = Object.keys(savedConsent.choices).filter((category) => savedConsent.choices[category] === true);

  if (!window.cookiedock.__consent) {
    window.cookiedock.__consent = {};
  }

  categories.forEach((category) => {
    window.cookiedock.__consent[category] = true;

    if (window.cookiedock.__activateCategory) {
      window.cookiedock.__activateCategory(category);
    } else {
      console.warn("Activation function not available yet");
    }
  });

  // Restore rejected cookies from storage (if available) and remove them now
  try {
    const raw = localStorage.getItem(CONSTANTS.REJECTED);
    if (raw) {
      const rejected: Rejected[] = JSON.parse(raw);
      if (!window.cookiedock.__cookies) {
        window.cookiedock.__cookies = { rejected: [], content: null } as any;
      }
      window.cookiedock.__cookies.rejected = rejected || [];
      if (rejected && rejected.length > 0) {
        removeRejectedCookies();
      }
    }
  } catch (e) {}

  return true;
}
